import { useT } from "@transifex/react";
import Checkbox from "../../../basic/checkbox/Checkbox";
import IconButton from "../../../basic/iconButton/IconButton";
import PhaseSixIcon from "../../../basic/phaseSixIcon/PhaseSixIcon";
import type { ClassMember } from "../../../networking/schoolClasses";
import useBreakpoints from "../../../hooks/useBreakpoints";
import ClassMemberJoinedLabel from "./ClassMemberJoinedLabel";
import NewClassMemberBadge from "./NewClassMemberBadge";

export interface ClassMemberRowSelection {
    isSelected: boolean;
    onToggle: () => void;
}

interface ClassMemberRowProps {
    classMember: ClassMember;
    index: number;
    numberClass: string;
    isNew: boolean;
    isInDuplicateGroup: boolean;
    /** Swaps the row actions for a checkbox. */
    selection?: ClassMemberRowSelection;
    onShareAccess: (classMember: ClassMember) => void;
    onEdit: (classMember: ClassMember) => void;
    onDelete: (classMember: ClassMember) => void;
}

const ClassMemberRow = (props: ClassMemberRowProps) => {
    const t = useT();
    const { isAboveMD: isDesktop } = useBreakpoints();

    const t_shareAccess = t("Share access", { _tags: "SchoolClasses, ClassMemberRow" });
    const t_sr_editClassMember = t("Edit student", {
        _tags: "SchoolClasses, ClassMemberRow, ScreenReaderOnly",
    });
    const t_sr_deleteClassMember = t("Delete student", {
        _tags: "SchoolClasses, ClassMemberRow, ScreenReaderOnly",
    });

    const rowTestId = `class-member-row-${props.classMember.id}`;

    const shareIcon = (
        <PhaseSixIcon
            name="share"
            className="text-xl shrink-0"
        />
    );

    // On mobile the label may wrap so a long translation never collides with the divider.
    const renderShareAccessButton = () => (
        <button
            type="button"
            data-testid="class-member-share"
            onClick={() => props.onShareAccess(props.classMember)}
            className={`flex items-center gap-xs bg-transparent border-none p-0 cursor-pointer text-p6-grey-dark hover:text-p6-grey-darkest ${
                isDesktop ? "shrink-0" : "min-w-0 text-left"
            }`}
        >
            {!isDesktop && shareIcon}
            <span className="text-m">{t_shareAccess}</span>
            {isDesktop && shareIcon}
        </button>
    );

    // Adjacent 44px tap targets; right-aligned glyphs keep the delete icon flush with the row
    // padding while the glyph gap stays the targets' free space.
    const renderDividerAndActions = (extraClass: string) => (
        <div className={`flex items-center ${extraClass}`}>
            <div className="self-stretch border-l border-p6-grey-light" />
            <div className="flex items-center shrink-0">
                <IconButton
                    data-testid="class-member-edit"
                    iconName="edit-avatar"
                    ariaLabel={t_sr_editClassMember}
                    hoverColor="text-p6-grey-darkest"
                    onClick={() => props.onEdit(props.classMember)}
                    size="text-icon-s"
                    iconAlignment="right"
                />
                <IconButton
                    data-testid="class-member-delete"
                    iconName="trash"
                    ariaLabel={t_sr_deleteClassMember}
                    hoverColor="text-p6-red"
                    onClick={() => props.onDelete(props.classMember)}
                    size="text-icon-s"
                    iconAlignment="right"
                />
            </div>
        </div>
    );

    // Checkbox gets no onClick: the wrapping button already toggles.
    const renderSelectionRow = (selection: ClassMemberRowSelection) => {
        const backgroundClass = selection.isSelected ? "bg-p6-orange-light-pastel" : "bg-white";
        const nameClass = `text-l text-p6-grey-dark min-w-0 [overflow-wrap:anywhere] ${
            selection.isSelected || !isDesktop ? "font-semibold" : ""
        }`;
        const nameLabel = (
            <span
                className={nameClass}
                data-testid="class-member-name"
            >
                {props.classMember.name}
            </span>
        );
        const joinedLabel = (
            <ClassMemberJoinedLabel
                classMember={props.classMember}
                isEmphasized={props.isNew}
            />
        );

        return (
            <div
                className={`${backgroundClass} border-t border-p6-grey-lightest`}
                data-testid={rowTestId}
                data-selected={selection.isSelected}
            >
                <button
                    type="button"
                    role="checkbox"
                    aria-checked={selection.isSelected}
                    data-testid="class-member-select"
                    onClick={selection.onToggle}
                    className={`flex items-center justify-between gap-s w-full bg-transparent border-none text-left cursor-pointer ${
                        isDesktop ? "px-m py-xs min-h-[60px]" : "p-s min-h-[44px]"
                    }`}
                >
                    <span className="flex items-center gap-s min-w-0">
                        <Checkbox isChecked={selection.isSelected} />
                        {isDesktop ? (
                            nameLabel
                        ) : (
                            <span className="flex flex-col gap-xxs min-w-0">
                                {nameLabel}
                                {joinedLabel}
                            </span>
                        )}
                    </span>
                    {isDesktop && joinedLabel}
                </button>
            </div>
        );
    };

    if (props.selection) return renderSelectionRow(props.selection);

    const backgroundClass = props.isInDuplicateGroup ? "bg-p6-buttercup-100" : "bg-white";

    if (isDesktop) {
        return (
            <div
                className={`flex items-center justify-between ${backgroundClass} border-t border-p6-grey-lightest px-m py-xs min-h-[60px] gap-s`}
                data-testid={rowTestId}
            >
                <div className="flex flex-1 min-w-0 items-center gap-l">
                    <span className={`text-l text-p6-grey-dark ${props.numberClass}`}>{props.index + 1}</span>
                    <span
                        className="text-l text-p6-grey-dark min-w-0 [overflow-wrap:anywhere]"
                        data-testid="class-member-name"
                    >
                        {props.classMember.name}
                    </span>
                    {props.isNew && <NewClassMemberBadge />}
                </div>
                <div className="flex gap-s items-center shrink-0">
                    {renderShareAccessButton()}
                    {renderDividerAndActions("")}
                </div>
            </div>
        );
    }

    return (
        <div
            className={`flex ${backgroundClass} p-s border-t border-p6-grey-lightest`}
            data-testid={rowTestId}
        >
            <div className="flex flex-col gap-xs flex-1 min-w-0">
                <div className="flex items-start gap-s font-semibold text-l text-p6-grey-dark">
                    <span className={props.numberClass}>{props.index + 1}</span>
                    <span
                        className="min-w-0 [overflow-wrap:anywhere]"
                        data-testid="class-member-name"
                    >
                        {props.classMember.name}
                    </span>
                    {props.isNew && <NewClassMemberBadge />}
                </div>
                {/* Deliberate deviation from the designs: spread share and edit/delete apart. */}
                <div className="flex items-center justify-between gap-s w-full">
                    {renderShareAccessButton()}
                    {renderDividerAndActions("shrink-0")}
                </div>
            </div>
        </div>
    );
};

export default ClassMemberRow;
